import { useState } from 'react';
import { useArrowProfiles } from '../hooks';
import type { ArrowProfile } from '../types';
import { newProfileId } from '../storage';
import { ArrowProfileForm } from './ArrowProfileForm';
import { useForgeLibrary } from '../useForgeLibrary';

const EMPTY_ARROW = (): ArrowProfile => ({
  profileId: newProfileId(),
  name: '',
  isActive: false,
  shaft: { brand: '', model: '', spine: 340, lengthIn: 28.5 },
  nock: { type: 'Standard', weightGr: 9 },
  vanes: { brand: '', model: '', count: 3, offsetDeg: 2 },
  insert: { brand: '', weightGr: 50 },
  point: { brand: '', model: '', type: 'COC fixed', weightGr: 100 },
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

export function ArrowsPage() {
  const { profiles, upsert, remove, setActive } = useArrowProfiles();
  const forge = useForgeLibrary();
  const [editing, setEditing] = useState<ArrowProfile | null>(null);
  const [creating, setCreating] = useState(false);

  function startCreate() {
    setEditing(EMPTY_ARROW());
    setCreating(true);
  }

  function startEdit(p: ArrowProfile) {
    setEditing({ ...p });
    setCreating(false);
  }

  function cancel() {
    setEditing(null);
    setCreating(false);
  }

  function save(p: ArrowProfile) {
    const isFirst = profiles.length === 0;
    upsert({ ...p, isActive: p.isActive || isFirst, updatedAt: Date.now() });
    setEditing(null);
    setCreating(false);
  }

  /* Copy a read-only imported Forge build into the editable My Arrows store.
   * User-initiated only; assigns a fresh profileId so it becomes a real,
   * editable profile rather than a phantom import. */
  function saveImported(p: ArrowProfile) {
    const now = Date.now();
    upsert({
      ...p,
      profileId: newProfileId(),
      isActive: profiles.length === 0,
      createdAt: now,
      updatedAt: now,
    });
  }

  /* Hide imported builds whose name already exists locally (avoids dupes
   * after a user has saved one). */
  const localNames = new Set(profiles.map((p) => p.name.trim().toLowerCase()));
  const importable = forge.arrows.filter((a) => !localNames.has(a.name.trim().toLowerCase()));

  return (
    <div className="profiles-page arrows-page">
      <nav className="forge-nav">
        <a href="https://sparrowexpeditions.com/pages/the-forge" target="_top">The Forge</a>
        <a href="/wind-drift.html">Wind-Drift</a>
        <a href="/build-review.html">Build Review</a>
        <a href="/tune.html">Tune Wizard</a>
        <a href="/range.html">Range Log</a>
        <a href="/bows.html">My Bows</a>
        <a href="/arrows.html" className="active">My Arrows</a>
        <a href="/pages/methods#ashby" target="_blank" rel="noopener">Methods</a>
        <a href="/blogs/field-notes/500-grain-eland-what-ashby-missed" target="_blank" rel="noopener">Field Notes</a>
      </nav>

      <header className="profiles-header">
        <div>
          <h1>My Arrows</h1>
          <p className="lede">
            Save your arrow builds. Each profile follows the wizard build flow — shaft → nock →
            vanes → insert → point. One arrow is <strong>active</strong> at a time.
          </p>
        </div>
        <button type="button" className="primary-cta" onClick={startCreate}>
          + Add Arrow
        </button>
      </header>

      {editing && (
        <div className="profile-form-shell">
          <ArrowProfileForm
            profile={editing}
            mode={creating ? 'create' : 'edit'}
            onSave={save}
            onCancel={cancel}
          />
        </div>
      )}

      <section className="profile-list">
        {profiles.length === 0 ? (
          <div className="empty-state">
            <p>No arrows yet. Add your first build to get started.</p>
            <button type="button" className="primary-cta" onClick={startCreate}>
              + Add your first arrow
            </button>
          </div>
        ) : (
          profiles.map((p) => (
            <article key={p.profileId} className={`profile-card ${p.isActive ? 'is-primary' : ''}`}>
              <div className="profile-card-head">
                <div>
                  <h2 className="profile-name">
                    {p.name || <em className="unnamed">Untitled arrow</em>}
                    {p.isActive && <span className="primary-pill">Active</span>}
                  </h2>
                  <p className="profile-sub">
                    {p.shaft.brand} {p.shaft.model} · spine {p.shaft.spine} · {p.shaft.lengthIn}″
                  </p>
                  <p className="profile-sub-faint">
                    {p.point.weightGr}gr {p.point.type} · {p.vanes.count} × {p.vanes.brand || p.vanes.model || 'vanes'}{' '}
                    {p.vanes.offsetDeg > 0 ? `(${p.vanes.offsetDeg}° offset)` : '(straight)'}
                  </p>
                </div>
                <div className="profile-actions">
                  {!p.isActive && (
                    <button type="button" onClick={() => setActive(p.profileId)} title="Set as active arrow">
                      Set active
                    </button>
                  )}
                  <button type="button" onClick={() => startEdit(p)}>Edit</button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => {
                      if (confirm(`Delete "${p.name}"?`)) remove(p.profileId);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </article>
          ))
        )}
      </section>

      {forge.status === 'ready' && importable.length > 0 && (
        <section className="profile-list forge-import-list">
          <h2 className="forge-import-heading">From your Forge builds</h2>
          <p className="forge-import-note">
            Arrows you saved in the Builder, Sketch, or Wizard. These are read-only here —
            tap <strong>Save to My Arrows</strong> to make one editable.
          </p>
          {importable.map((p) => (
            <article key={p.profileId} className="profile-card is-imported">
              <div className="profile-card-head">
                <div>
                  <h2 className="profile-name">
                    {p.name || <em className="unnamed">Untitled arrow</em>}
                    <span className="imported-pill">Forge</span>
                  </h2>
                  <p className="profile-sub">
                    {p.shaft.brand} {p.shaft.model} · spine {p.shaft.spine} · {p.shaft.lengthIn}″
                  </p>
                  <p className="profile-sub-faint">
                    {p.point.weightGr}gr {p.point.type} · {p.vanes.count} × {p.vanes.model || 'vanes'}{' '}
                    {p.vanes.offsetDeg > 0 ? `(${p.vanes.offsetDeg}° offset)` : '(straight)'}
                  </p>
                </div>
                <div className="profile-actions">
                  <button type="button" className="primary-cta" onClick={() => saveImported(p)}>
                    Save to My Arrows
                  </button>
                </div>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
